import settings from 'helpers/settings';

const normalize = (path: string) => path.replace(/\/+$/, '') || '/';

/**
 * Some nav items land on a *different* path than their configured
 * `target_url` depending on business rules that can't be inferred from the
 * URL alone - unlike a route such as `/certifications`, which merely
 * redirects to a sub-path of itself (handled generically below), these
 * redirect somewhere with no shared prefix at all. Maps a nav item's
 * configured path to any other path(s) that should also count as "current"
 * for it.
 *
 * - `/coach/learning-resources` ("Learning Resources 2.0"): users without
 *   the `lr_coach` role are redirected to `/learning-resources` instead -
 *   see the `useEffect` in `pages/Coach/Coach.tsx`.
 */
const REDIRECT_ALIASES: Record<string, string[]> = {
  '/coach/learning-resources': ['/learning-resources']
};

function pathMatches(currentPath: string, targetPath: string): boolean {
  return targetPath === '/'
    ? currentPath === '/'
    : currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
}

function originOf(url?: string | null): string | undefined {
  if (!url) return undefined;
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
}

/**
 * Pure matching core, independent of `window`/`settings` so it's cheap to
 * unit test with explicit inputs - see `isCurrentPage` below for the
 * browser-facing entry point most callers should use.
 *
 * A nav item is considered current if `targetUrl` resolves to one of
 * `allowedOrigins` (anything else - an external link like Canvas or
 * Community - is rejected outright, regardless of whether its path happens
 * to collide with an internal one) AND the current path is that target's
 * path, or nested under it - e.g. a "Certifications" item targeting
 * `/certifications` should still read as current on
 * `/certifications/organization`, since routes like that one client-side
 * redirect their index route to a subpage. `/` (Home) is the one exception:
 * it only matches the exact root, otherwise every page in the app would
 * count as "current" for Home. See `REDIRECT_ALIASES` above for items that
 * redirect to an unrelated path.
 */
export function matchesCurrentPage(
  currentPathname: string,
  targetUrl: string | null | undefined,
  browserOrigin: string,
  allowedOrigins: ReadonlyArray<string> = [browserOrigin]
): 'page' | undefined {
  if (!targetUrl) return undefined;

  let target: URL;
  try {
    target = new URL(targetUrl, browserOrigin);
  } catch {
    return undefined;
  }

  if (!allowedOrigins.includes(target.origin)) return undefined;

  const currentPath = normalize(currentPathname);
  const targetPath = normalize(target.pathname);
  const candidatePaths = [targetPath, ...(REDIRECT_ALIASES[targetPath] ?? [])];

  const isMatch = candidatePaths.some(candidatePath =>
    pathMatches(currentPath, candidatePath)
  );

  return isMatch ? 'page' : undefined;
}

/**
 * Determines whether a nav item's target URL points at the page (or section)
 * currently being viewed, so callers can mark that item with
 * `aria-current="page"`.
 *
 * `targetUrl` may be a relative path (e.g. `/trainings`) or an absolute URL.
 * The backend builds this app's own nav links (Certifications, Organization,
 * Measure, etc.) as absolute URLs from `ONELOGIN_BASE_URL`, which doesn't
 * necessarily match the browser's actual origin - e.g. in local dev the
 * frontend runs on :4002 while `ONELOGIN_BASE_URL` points at the Rails
 * server on :3002. So a same-origin nav link is accepted, and so is one
 * whose origin matches the configured `ONELOGIN_BASE_URL` (also this app,
 * just addressed differently) - anything else is a genuinely external link
 * (Canvas, Community, myCLASS, teachstone.com, ...) and is rejected before
 * paths are ever compared, so it can never falsely match on a coincidental
 * same path.
 */
export function isCurrentPage(
  currentPathname: string,
  targetUrl?: string | null
): 'page' | undefined {
  const browserOrigin = window.location.origin;
  const oneloginOrigin = originOf(settings.lookup('ONELOGIN_BASE_URL'));

  const allowedOrigins = oneloginOrigin
    ? [browserOrigin, oneloginOrigin]
    : [browserOrigin];

  return matchesCurrentPage(
    currentPathname,
    targetUrl,
    browserOrigin,
    allowedOrigins
  );
}
